import { useState, useRef, useMemo, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { Modal } from 'react-bootstrap'
import { IconButton, Box, CircularProgress } from '@mui/material'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { showImagePreview } from 'slices/customer'
import configs from 'config/environment'
import { MaskedImage } from 'components'
import {
  CreditCardMaskEditor,
  CreditCardMaskEditorControls,
  CreditCardMaskEditorCanvas
} from 'components'
import { getVaultUrlForPreviewSession } from 'utils/vaultPreviewSnapshot'

const isAbsoluteUrl = value =>
  typeof value === 'string' && /^(https?:)?\/\//i.test(value)

const getFileDocId = file =>
  file?.file_obj?.document_revision_id ||
  file?.document_revision_id ||
  file?.id ||
  null

const getFileName = file =>
  file?.file_obj?.name ||
  file?.file_obj?.file_name ||
  file?.name ||
  file?.fileName ||
  'Card image'

const getVaultUrlForIndex = (unmaskedCards, cardId, index) => {
  const data = cardId ? unmaskedCards?.[cardId] : null
  if (!data) return null
  if (Array.isArray(data.images)) {
    return data.images.find(img => img.index === index + 1)?.url ?? null
  }
  return index === 0 ? data.imageUrl : null
}

const ImagePreview = () => {
  const dispatch = useDispatch()
  const { imagePreview, unmaskedCards } = useSelector(state => state.customer)
  const paymentCards = useSelector(state => state.paymentCards?.paymentCards)
  const { currentTab } = useSelector(state => state.customerSteps)
  const userData = useSelector(state => state?.auth?.data)
  const currentUserInformation = userData?.current_user
  const userRole = currentUserInformation?.affiliate_role || false
  const isAuthAccess = userRole === 'auth_access'
  const normalizedDocType = (imagePreview.documentType || '').toLowerCase()
  const isCreditCardImageType =
    normalizedDocType === 'credit_card_image' ||
    normalizedDocType === 'credit_card'
  const isCreditCard = isCreditCardImageType || imagePreview.cardId != null
  const isNewRelation = !!imagePreview.isNewRelation
  const currentCardAttributes = useMemo(() => {
    if (!imagePreview.cardId || !Array.isArray(paymentCards)) return null
    const match = paymentCards.find(
      card => card?.attributes?.id === imagePreview.cardId
    )
    return match?.attributes || null
  }, [imagePreview.cardId, paymentCards])

  const cardOcrStatus = currentCardAttributes?.ocr_status
  const cardLuhnCheck = currentCardAttributes?.luhn_check
  const cardBinCheck = currentCardAttributes?.bin_check
  const cardRedactionVerified = currentCardAttributes?.redaction_verified
  const hasRedactionPassed =
    isCreditCard &&
    cardOcrStatus === '1' &&
    // cardLuhnCheck === '1' &&
    // cardBinCheck === '1' &&
    cardRedactionVerified === '1'
  const shouldMask =
    isCreditCard &&
    !isNewRelation &&
    !hasRedactionPassed &&
    !(isAuthAccess && cardRedactionVerified === '0')
  const [rotate, setRotate] = useState(0)
  const [imageLoaded, setImageLoaded] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [imageRefreshKey, setImageRefreshKey] = useState(0)
  const imageRef = useRef(null)
  const containerRef = useRef(null)
  const maskedImageContainerRef = useRef(null)
  const refreshKey = imagePreview?.refreshKey || imageRefreshKey || 0

  const cardFiles = useMemo(() => {
    if (!isCreditCard || !imagePreview.cardId || !Array.isArray(paymentCards)) {
      return []
    }
    const card = paymentCards.find(
      c => c?.attributes?.id === imagePreview.cardId
    )
    return (card?.files || []).filter(Boolean)
  }, [isCreditCard, imagePreview.cardId, paymentCards])

  const activeIndex = useMemo(() => {
    if (cardFiles.length === 0) return imagePreview?.imageIndex || 0
    const index = imagePreview?.imageIndex ?? 0
    return Math.min(Math.max(index, 0), cardFiles.length - 1)
  }, [cardFiles.length, imagePreview?.imageIndex])

  const activeFile = cardFiles[activeIndex] || null
  const activeDocId = getFileDocId(activeFile) || imagePreview.src
  const hasMultipleCardImages = isCreditCard && cardFiles.length > 1

  const isVaultPreview =
    !!imagePreview?.isVaultSource ||
    isAbsoluteUrl(imagePreview?.src) ||
    !!(imagePreview?.cardId && unmaskedCards?.[imagePreview.cardId])
  const imageUrl = useMemo(() => {
    if (isVaultPreview) {
      const vaultUrlFromSession = getVaultUrlForPreviewSession(
        imagePreview,
        activeIndex
      )
      if (vaultUrlFromSession) return vaultUrlFromSession

      const vaultUrl = getVaultUrlForIndex(
        unmaskedCards,
        imagePreview.cardId,
        activeIndex
      )
      if (vaultUrl) return vaultUrl
      if (isAbsoluteUrl(imagePreview.src)) return imagePreview.src
    }

    const fileId = activeDocId
    if (!fileId || isAbsoluteUrl(fileId)) return imagePreview.src

    const shouldRequestRedacted = isCreditCard && hasRedactionPassed
    const maskParam = shouldRequestRedacted ? '&mask=1' : ''
    const baseUrl = `${configs.apiUrl}/index.php?entryPoint=Image&download=0&preview=0&file=${fileId}${maskParam}`
    return refreshKey > 0 ? `${baseUrl}&_t=${Date.now() + refreshKey}` : baseUrl
  }, [
    activeDocId,
    activeIndex,
    imagePreview.src,
    imagePreview.cardId,
    imagePreview.isVaultSource,
    imagePreview.vaultImageUrl,
    imagePreview.vaultImages,
    refreshKey,
    isCreditCard,
    hasRedactionPassed,
    isVaultPreview,
    unmaskedCards
  ])

  const resetPreviewViewState = useCallback(() => {
    setRotate(0)
    setImageLoaded(false)
    setLoadError(false)
    setIsEditMode(false)
    setImageRefreshKey(0)
  }, [])

  const switchPreviewImage = useCallback(
    newIndex => {
      if (newIndex < 0 || newIndex >= cardFiles.length) return
      if (isEditMode) return

      const file = cardFiles[newIndex]
      const docId = getFileDocId(file)
      if (!docId) return

      const isNewRelation = !!(
        file?.new_relation_addition || file?.file_obj?.new_relation_addition
      )

      dispatch(
        showImagePreview({
          name: getFileName(file),
          docId,
          preview: true,
          documentType: imagePreview.documentType,
          cardId: imagePreview.cardId,
          imageIndex: newIndex,
          isNewRelation
        })
      )
      resetPreviewViewState()
    },
    [
      cardFiles,
      dispatch,
      imagePreview.cardId,
      imagePreview.documentType,
      imagePreview.isNewRelation,
      isEditMode,
      resetPreviewViewState
    ]
  )

  const handlePrevImage = useCallback(() => {
    switchPreviewImage(activeIndex === 0 ? cardFiles.length - 1 : activeIndex - 1)
  }, [activeIndex, cardFiles.length, switchPreviewImage])

  const handleNextImage = useCallback(() => {
    switchPreviewImage(
      activeIndex === cardFiles.length - 1 ? 0 : activeIndex + 1
    )
  }, [activeIndex, cardFiles.length, switchPreviewImage])

  const handleClose = () => {
    dispatch(
      showImagePreview({
        docId: '',
        preview: false,
        isVaultSource: false,
        vaultImageUrl: null,
        vaultImages: null
      })
    )

    setRotate(0)
    setImageLoaded(false)
    setLoadError(false)
    setIsEditMode(false)
    setImageRefreshKey(0)
    // setIsPreviewReAuthOpen(false)
  }

  const rotateLeft = () => {
    let newRotation = rotate + 90
    if (newRotation >= 360) {
      newRotation = -360
    }
    setRotate(newRotation)
  }

  const handleToggleEditMode = editMode => {
    setIsEditMode(editMode)
  }

  useEffect(() => {
    setImageRefreshKey(0)
    setImageLoaded(false)
    setLoadError(false)
    setIsEditMode(false)
    if (containerRef.current) {
      containerRef.current.scrollTop = 0
    }
  }, [imagePreview.src, imagePreview.imageIndex])



  const handleSaveSuccess = response => {
    if (response?.success === true) {
      setIsEditMode(false)
      handleClose()
    }
  }

  const handlePreviewContextMenu = e => {
    e.preventDefault()
    e.stopPropagation()
    if (!isEditMode) {
      rotateLeft()
    }
    return false
  }

  const handlePreviewDragStart = e => {
    if (isCreditCard) {
      e.preventDefault()
      e.stopPropagation()
      return false
    }
  }

  if (!imagePreview.preview) return null

  const canShowAuthEditIcon =
    isAuthAccess &&
    currentTab !== 2 &&
    isCreditCard


  return (
    <CreditCardMaskEditor
      imagePreview={imagePreview}
      imageLoaded={imageLoaded}
      imageRef={imageRef}
      containerRef={containerRef}
      maskedImageContainerRef={maskedImageContainerRef}
      shouldMask={shouldMask}
      isOcrComplete={cardOcrStatus === '1'}
      onToggleEditMode={handleToggleEditMode}
      onSaveSuccess={handleSaveSuccess}
    >
      <Modal
        show={imagePreview.preview}
        onHide={handleClose}
        centered
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        enforceFocus={false}
        autoFocus={false}
        restoreFocus={false}
        style={{ transform: `rotate(${rotate}deg)` }}
      >
        <Modal.Header style={{ position: 'relative' }}>
          {isCreditCard &&
            imagePreview.preview &&
            canShowAuthEditIcon && (
              <CreditCardMaskEditorControls />
            )}
        </Modal.Header>
        <PreviewViewport
          as={Modal.Body}
          data-toggle="tooltip"
          data-placement="top"
          title={
            isEditMode
              ? 'Click and drag to draw rectangular masks'
              : 'Click right button to rotate'
          }
        >
          {hasMultipleCardImages && (
            <PreviewChrome>
              <PreviewNavigation
                role="toolbar"
                aria-label="Card image navigation"
              >
                <NavButton
                  onClick={handlePrevImage}
                  disabled={isEditMode}
                  aria-label="Previous card image"
                  size="small"
                >
                  <ChevronLeft />
                </NavButton>
                <ImageCounter>
                  {activeIndex + 1} / {cardFiles.length}
                </ImageCounter>
                <NavButton
                  onClick={handleNextImage}
                  disabled={isEditMode}
                  aria-label="Next card image"
                  size="small"
                >
                  <ChevronRight />
                </NavButton>
              </PreviewNavigation>
            </PreviewChrome>
          )}
          <PreviewScroller ref={containerRef}>
            {!shouldMask && !imageLoaded && !loadError && (
              <PreviewLoader>
                <CircularProgress size={40} sx={{ color: '#F6C44B' }} />
              </PreviewLoader>
            )}
            {!shouldMask && loadError && (
              <PreviewError>Unable to load image. Please try again.</PreviewError>
            )}
            <ImageContainer>
              {shouldMask ? (
                <MaskedImageWrapper ref={maskedImageContainerRef}>
                  <MaskedImage
                    key={`masked-${activeDocId}-${activeIndex}-${refreshKey}`}
                    url={activeDocId}
                    item={activeFile || imagePreview}
                    cardId={imagePreview.cardId}
                    alt="Preview"
                    width="100%"
                    imageIndex={activeIndex}
                    refreshKey={
                      refreshKey > 0 ? Date.now() + refreshKey : null
                    }
                    onLoad={() => setImageLoaded(true)}
                    onError={() => setLoadError(true)}
                    onContextMenu={e => {
                      e.preventDefault()
                      if (!isEditMode) {
                        rotateLeft()
                      }
                    }}
                  />
                  {isCreditCard && (
                    <CreditCardMaskEditorCanvas imageLoaded={imageLoaded} />
                  )}
                </MaskedImageWrapper>
              ) : (
                <ImageWrapper>
                  <StyledImage
                    key={`preview-${activeDocId}-${activeIndex}-${refreshKey}`}
                    ref={imageRef}
                    src={imageUrl}
                    alt="Preview"
                    onLoad={() => setImageLoaded(true)}
                    onError={() => setLoadError(true)}
                    draggable={isCreditCard ? false : undefined}
                    onDragStart={handlePreviewDragStart}
                    onContextMenu={handlePreviewContextMenu}
                  />
                  {isCreditCard && (
                    <CreditCardMaskEditorCanvas imageLoaded={imageLoaded} />
                  )}
                </ImageWrapper>
              )}
            </ImageContainer>
          </PreviewScroller>
        </PreviewViewport>
        <Modal.Footer></Modal.Footer>
      </Modal>
    </CreditCardMaskEditor>
  )
}

const StyledImage = styled.img`
  width: 100%;
  display: block;
`

const PreviewViewport = styled.div`
  position: relative;
  height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`

const PreviewChrome = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 10;
`

const PreviewScroller = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
`

const PreviewLoader = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
  pointer-events: none;
`

const PreviewError = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
  padding: 16px;
  text-align: center;
  color: #b00020;
  font-size: 14px;
`

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
`

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
`

const MaskedImageWrapper = styled.div`
  position: relative;
  width: 100%;
`

const PreviewNavigation = styled(Box)`
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 20px;
  padding: 5px 10px;
  pointer-events: auto;
`

const NavButton = styled(IconButton)`
  color: white !important;
  padding: 4px !important;

  &:hover {
    background: rgba(255, 255, 255, 0.1) !important;
  }

  &:disabled {
    color: rgba(255, 255, 255, 0.4) !important;
  }
`

const ImageCounter = styled.span`
  color: white;
  font-size: 12px;
  margin: 0 8px;
  font-weight: 500;
  min-width: 48px;
  text-align: center;
`

export default ImagePreview
